
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Position, type EdgeProps } from '@xyflow/react'
import { cablePath, cableRoute, laneAxis, type CrossingStyle, type Jump } from '@/lib/cable-route'

const NO_JUMPS: Jump[] = []

/**
 * A cable, routed into its own lane when it shares both ends with others, and
 * giving way where it crosses another when the Crossings setting asks for it.
 *
 * The lane offset is computed once for the whole canvas (see `assignLanes`)
 * and handed down as `data.laneOffset`; it shifts the corner the route turns
 * at, perpendicular to the run. The result is parallel cables at a fixed
 * spacing rather than one line pretending to be four. Crossings are found in
 * the same pass (see `findCrossings`) and arrive as `data.jumps`, with the
 * style to draw them in as `data.crossings`. A selected cable never gets
 * jumps; the cables it crosses carry them instead.
 */
export function CableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  label,
  selected,
  data,
}: EdgeProps) {
  const offset = typeof data?.laneOffset === 'number' ? data.laneOffset : 0
  const jumps = Array.isArray(data?.jumps) ? (data.jumps as Jump[]) : NO_JUMPS
  const crossings = (data?.crossings as CrossingStyle | undefined) ?? 'off'

  let path: string
  let caps = ''
  let labelX: number
  let labelY: number
  if (sourcePosition === Position.Right && targetPosition === Position.Left) {
    // Every cable leaves a right-hand output for a left-hand input, which is
    // the case cableRoute ports from React Flow's smooth-step routing.
    const route = cableRoute(sourceX, sourceY, targetX, targetY, offset)
    ;({ path, caps } = cablePath(route.points, jumps, crossings))
    labelX = route.label.x
    labelY = route.label.y
  } else {
    const axis = laneAxis(sourceX, sourceY, targetX, targetY)
    ;[path, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      borderRadius: 8,
      centerX: axis === 'x' ? (sourceX + targetX) / 2 + offset : undefined,
      centerY: axis === 'y' ? (sourceY + targetY) / 2 + offset : undefined,
    })
  }

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={style}
        markerEnd={markerEnd}
        // A selected cable lifts above its neighbours so you can see which of
        // a tight bundle you actually grabbed.
        interactionWidth={20}
      />
      {caps ? (
        // Solid even on a dashed cable: the bars are what say the break is
        // deliberate, so the dash pattern must not swallow them.
        <path d={caps} className="cable-caps" fill="none" style={{ stroke: style?.stroke }} />
      ) : null}
      {selected ? (
        // Beads running from the output to the input: which cable you have
        // hold of, and which way its signal goes. See .cable-flow.
        <path d={path} className="cable-flow" fill="none" style={{ stroke: style?.stroke }} />
      ) : null}
      {label ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-none absolute rounded-full bg-surface/90 px-1.5 py-0.5 font-mono text-[9px] text-muted shadow-sm backdrop-blur"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              outline: selected ? '1px solid var(--ring)' : undefined,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  )
}
